/**
 * أداة تحميل الملفات متوافقة مع الجوال (PWA / WebView)
 *
 * الاستراتيجية:
 * 1. على الجوال: تجربة Web Share API أولاً (يسمح بحفظ الصور في الاستديو)
 * 2. Fallback: فتح الرابط في تاب جديد (الضغط المطول → حفظ الصورة)
 * 3. على الويب: تحميل عبر عنصر <a download>
 */

export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

const isShareSupported = () => {
  return typeof navigator !== 'undefined' && navigator.share && navigator.canShare;
};

const canShareFile = (file) => {
  if (!isShareSupported()) return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
};

/**
 * تحميل ملف وسائط (صورة أو فيديو) مع دعم الجوال
 * @param {Blob} blob - محتوى الملف
 * @param {string} fileName - اسم الملف
 * @param {string} mimeType - نوع MIME (مثل image/jpeg)
 * @returns {Promise<{ success: boolean, method?: string }>}
 */
export const downloadMediaFile = async (blob, fileName, mimeType) => {
  const file = new File([blob], fileName, { type: mimeType || blob.type });

  // 1. على الجوال: تجربة Web Share API (يُظهر خيار "حفظ الصورة" على iOS)
  if (isMobile() && canShareFile(file)) {
    try {
      await navigator.share({
        files: [file],
        title: fileName,
        text: 'صورة من جلسة التصوير'
      });
      return { success: true, method: 'share' };
    } catch (err) {
      if (err.name === 'AbortError') return { success: true, method: 'share_cancelled' };
      // المتابعة للـ fallback
    }
  }

  // 2. Fallback للجوال: فتح في تاب جديد (الضغط المطول → حفظ)
  if (isMobile()) {
    try {
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank', 'noopener');
      if (win) {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        return { success: true, method: 'open_tab' };
      }
      URL.revokeObjectURL(blobUrl);
    } catch {
      // المتابعة للتحميل العادي
    }
  }

  // 3. تحميل عادي (يعمل على الويب، قد لا يعمل في بعض WebViews)
  try {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }, 100);
    return { success: true, method: 'download' };
  } catch (err) {
    console.error('downloadMediaFile error:', err);
    return { success: false };
  }
};

/**
 * تحميل ملف من URL (fetch ثم downloadMediaFile)
 */
export const downloadMediaFromUrl = async (url, fileName, mimeType) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch file');
  const blob = await response.blob();
  const type = (mimeType && mimeType.includes('/')) ? mimeType : (blob.type || 'application/octet-stream');
  return downloadMediaFile(blob, fileName, type);
};
